import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from '@mui/material';
import { SubscriptionOption, PlanSummary } from '../../types';
import getUpgradePreview from '../../api/getUpgradePreview';
import useSubscriptionOptions from './useSubscriptionOptions';
import useBraintreeDropIn from './useBraintreeDropIn';
import useStepper from './useStepper';
import processPlanDowngrade from '../../api/processPlanDowngrade';
import SegmentService from '../../../lib/segment/SegmentService';
import { SEGMENT_EVENTS } from '../../../lib/segment/SegmentEvents';

export const DEFAULT_PLAN_PREVIEW: PlanSummary = {
    coachLicenseAmount: 0,
    assistantCoachLicenseCount: 0,
    assistantCoachLicenseAmount: 0,
    subtotalDueNextBillingDate: 0,
    totalDueNextBillingDate: 0,
    nextBillingDate: '',
    proratedCoachLicenseAmount: 0,
    totalDueToday: 0,
    taxAmountDueNextBillingDate: null,
    proratedCoachLicenseTaxAmount: null,
    hasProratedBilling: false,
    hasTaxError: false
};

export const DEFAULT_ERROR_MESSAGE = (
    <>
        Something went wrong while processing your request. Please try again. If the issue persists, contact{' '}
        <Link href="https://support.trainheroic.com/hc/en-us/requests/new">Support</Link>.
    </>
);
export default function useSubscriptionPlanCheckout() {
    const [initialPlanSummary, setInitialPlanSummary] = useState<PlanSummary>(DEFAULT_PLAN_PREVIEW);
    const [finalPlanSummary, setFinalPlanSummary] = useState<PlanSummary>(DEFAULT_PLAN_PREVIEW);
    const [isReviewPurchaseLoading, setIsReviewPurchaseLoading] = useState(false);
    const [maxAthleteCount, setMaxAthleteCount] = useState(0);
    const [successMessage, setSuccessMessage] = useState('');
    const [shouldShowSuccessDialog, setShouldShowSuccessDialog] = useState(false);
    const [hasServerError, setHasServerError] = useState(false);
    const [serverErrorMessage, setServerErrorMessage] = useState<string | React.ReactNode>('');
    const [serverErrorTitle, setServerErrorTitle] = useState<string>('');

    const orgId = useSelector((state: any) => state.user.headCoach?.orgId);

    const stepperSteps = ['Choose Plan', 'Payment', 'Review'];
    const { steps, activeStep, handleNext, handleBack, handleReset } = useStepper(stepperSteps);

    const handleServerError = () => {
        setHasServerError(true);
        setServerErrorTitle('Processing Error');
        setServerErrorMessage(DEFAULT_ERROR_MESSAGE);
        setIsReviewPurchaseLoading(false);
    };

    const handleTaxErrorFailure = () => {
        setHasServerError(true);
        setIsReviewPurchaseLoading(false);
        setServerErrorTitle('Payment Method Error');
        setServerErrorMessage(
            <>
                Something went wrong while processing your payment. Please try updating your payment method. If the
                issue persists, contact{' '}
                <Link href="https://support.trainheroic.com/hc/en-us/requests/new">Support</Link>.
            </>
        );
    };

    const handlePaymentPreview = async (plan: SubscriptionOption) => {
        const response = await getUpgradePreview(orgId, plan.coachLicenseId, '');

        if (response.status) {
            handleServerError();
            return;
        }
        setInitialPlanSummary(response);
        setMaxAthleteCount(plan.maxAthletes);
        handleNext();
    };

    const {
        isLoading: isLoadingSubscriptionOptions,
        subscriptionOptions,
        currentPlan,
        selectedPlan,
        setSelectedPlan,
        hasErrorLoadingOptions,
        setHasErrorLoadingOptions,
        fetchSubscriptionOptions
    } = useSubscriptionOptions();

    const {
        setBraintreeInstance,
        clientToken,
        isLoading: isLoadingBraintreeDropIn,
        createPaymentMethod,
        processPurchase,
        isPaymentProcessing,
        hasPaymentSuccess,
        setHasPaymentFailure,
        hasPaymentFailure,
        setHasPaymentSuccess,
        errorMessage,
        hasCreatePaymentMethodFailure,
        clearErrorMessage: clearBraintreeErrorMessage
    } = useBraintreeDropIn();

    const handleScrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDowngrade = async () => {
        const response = await processPlanDowngrade(orgId, selectedPlan.coachLicenseId);

        if (response.status && response.status !== 200) {
            handleServerError();
            return;
        }

        setSuccessMessage('Your plan has been downgraded.');
        setShouldShowSuccessDialog(true);
    };

    const handleReviewPurchase = async () => {
        setIsReviewPurchaseLoading(true);
        const paymentMethodId = await createPaymentMethod();
        if (!paymentMethodId) {
            setIsReviewPurchaseLoading(false);
            return;
        }

        const response = await getUpgradePreview(orgId, selectedPlan.coachLicenseId, paymentMethodId);

        if (response.status) {
            handleServerError();
            return;
        }

        if ((response as PlanSummary).hasTaxError) {
            handleTaxErrorFailure();
            return;
        }
        setIsReviewPurchaseLoading(false);
        setFinalPlanSummary(response);
        handleScrollToTop();
        handleNext();
    };

    const handlePurchase = async () => {
        SegmentService.track(SEGMENT_EVENTS.FeatureEngaged.key, {
            userType: 'Coach',
            location: `Coach Plans - Review`,
            feature: 'Purchase'
        });
        const didSucceed = await processPurchase(selectedPlan.coachLicenseId);
        if (didSucceed) {
            setShouldShowSuccessDialog(true);
            setSuccessMessage('Your plan has been upgraded.');
        }
    };

    const clearServerError = () => {
        setHasServerError(false);
    };

    const handleSuccessDialogClose = async () => {
        handleReset();
        setInitialPlanSummary(DEFAULT_PLAN_PREVIEW);
        setMaxAthleteCount(0);
        setShouldShowSuccessDialog(false);
        setHasPaymentFailure(false);
        setHasPaymentSuccess(false);
        setHasErrorLoadingOptions(false);
        await fetchSubscriptionOptions();
    };

    return {
        steps,
        activeStep,
        handleDowngrade,
        handlePaymentPreview,
        handleBack,
        successMessage,
        maxAthleteCount,
        initialPlanSummary,
        finalPlanSummary,
        isLoadingSubscriptionOptions,
        subscriptionOptions,
        currentPlan,
        selectedPlan,
        setSelectedPlan,
        hasErrorLoadingOptions,
        setHasErrorLoadingOptions,
        setBraintreeInstance,
        clientToken,
        isLoadingBraintreeDropIn,
        handleReviewPurchase,
        createPaymentMethod,
        handlePurchase,
        isReviewPurchaseLoading,
        isPaymentProcessing,
        hasPaymentSuccess,
        hasPaymentFailure,
        shouldShowSuccessDialog,
        handleSuccessDialogClose,
        hasCreatePaymentMethodFailure,
        errorMessage,
        clearServerError,
        hasServerError,
        serverErrorTitle,
        serverErrorMessage,
        clearBraintreeErrorMessage
    };
}
